import { useState, useEffect, useRef } from "react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Label } from "../components/ui/label";
import { 
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import { toast } from "sonner";
import { 
  Phone, 
  ArrowRight, 
  CheckCircle2, 
  Star, 
  Cog, 
  Target, 
  Shield, 
  Users,
  Factory,
  Truck,
  Plane,
  Ship,
  Wrench,
  Building2,
  Layers,
  MapPin,
  Clock,
  Facebook,
  Send,
  Loader2,
  Gauge,
  Ruler,
  Award,
  ChevronDown,
  Sparkles
} from "lucide-react";
import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Intersection Observer Hook for reveal animations
const useReveal = () => {
  useEffect(() => {
    const reveals = document.querySelectorAll('.reveal, .reveal-left, .reveal-right, .reveal-scale, .stagger-children');
    
    const observer = new IntersectionObserver((entries) => {
      entries.forEach(entry => {
        if (entry.isIntersecting) {
          entry.target.classList.add('active');
        }
      });
    }, { threshold: 0.1, rootMargin: '0px 0px -50px 0px' });

    reveals.forEach(reveal => observer.observe(reveal));
    
    return () => reveals.forEach(reveal => observer.unobserve(reveal));
  }, []);
};

// Counter animation hook
const useCounter = (end, duration = 2000, startOnView = true) => {
  const [count, setCount] = useState(0);
  const [hasStarted, setHasStarted] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    if (!startOnView) {
      animateCount();
      return;
    }

    const observer = new IntersectionObserver((entries) => {
      if (entries[0].isIntersecting && !hasStarted) {
        setHasStarted(true);
        animateCount();
      }
    }, { threshold: 0.5 });

    if (ref.current) observer.observe(ref.current);
    return () => observer.disconnect();
  }, [hasStarted]);

  const animateCount = () => {
    let startTime;
    const step = (timestamp) => {
      if (!startTime) startTime = timestamp;
      const progress = Math.min((timestamp - startTime) / duration, 1);
      setCount(Math.floor(progress * end));
      if (progress < 1) requestAnimationFrame(step);
    };
    requestAnimationFrame(step);
  };

  return { count, ref };
};

const services = [
  { title: "CNC Milling", description: "Precision multi-axis milling up to 900mm × 550mm — complex geometries to exacting tolerances.", icon: Cog },
  { title: "CNC Turning", description: "High-precision turning up to 375mm diameter on Yang, Dugard and FEMCO bar-fed lathes.", icon: Target },
  { title: "Manual & Semi-Auto", description: "Manual and semi-automatic milling, turning and grinding for short runs and one-offs.", icon: Wrench },
  { title: "Grinding Services", description: "Internal, external and surface grinding (CNC and manual) for the tightest finishes.", icon: Gauge },
  { title: "Product Assemblies", description: "Coded welding, component assembly and finished build — delivered ready to install.", icon: Layers },
  { title: "Finishing & Treatments", description: "Plating, heat treatments, anodising, painting and special finishes — completed in-house network.", icon: Sparkles },
  { title: "Keywaying & Slotting", description: "All keywaying and slotting requirements on a Butler 10\" slotter and turret mills.", icon: Ruler },
  { title: "Rapid Prototyping", description: "One-off prototypes and pre-production samples for R&D — fast, accurate, repeatable.", icon: Factory },
  { title: "Bespoke Components", description: "Single items to high-volume batches, machined to your exact drawings and specifications.", icon: Building2 },
];

const whyChooseUs = [
  { title: "Concept To Production", description: "High-quality solutions from initial concept through to production — we work closely with you to produce your exact requirements." },
  { title: "Wealth Of Expertise", description: "A skilled engineering team backed by decades of combined experience and continually invested-in training." },
  { title: "State-Of-The-Art Facilities", description: "Continuous investment in high-specification CNC machining centres, grinders and inspection equipment." },
  { title: "On Time, Every Time", description: "Working to demanding deadlines with a full UK delivery service — supplied on time, every time, within budget." },
];

const industries = [
  { name: "Valves & Conveyors", icon: Cog },
  { name: "Hydraulics", icon: Gauge },
  { name: "Packaging", icon: Layers },
  { name: "Machinery", icon: Factory },
  { name: "Manufacturing", icon: Building2 },
  { name: "Any Sector", icon: Wrench },
];

const materials = [
  { name: "Aluminium", description: "All grades" },
  { name: "Stainless Steel", description: "All types" },
  { name: "Duplex Alloys", description: "Specialist grades" },
  { name: "Acid Resistant", description: "Specialist alloys" },
  { name: "Brass & Copper", description: "All grades" },
  { name: "Aluminium Bronze", description: "Bearing grades" },
  { name: "Hastelloy", description: "All grades" },
  { name: "Inconel", description: "All grades" },
  { name: "Mild & Tool Steel", description: "EN8, EN24, more" },
  { name: "Plastics", description: "Nylon, Delrin, PEEK" },
];

const serviceOptions = ["CNC Milling", "CNC Turning", "Manual / Semi-Auto Machining", "Grinding (Internal / External / Surface)", "Product Assemblies", "Plating & Heat Treatments", "Anodising / Painting / Finishing", "Coded Welding", "Keywaying & Slotting", "Rapid Prototyping", "Other"];

export const HomePage = () => {
  useReveal();
  
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [formData, setFormData] = useState({ name: "", email: "", phone: "", company: "", service_interest: "", message: "" });
  const [errors, setErrors] = useState({});
  const [mousePosition, setMousePosition] = useState({ x: 0, y: 0 });
  const heroVideoRef = useRef(null);

  // Force the hero video to autoplay (works around iOS/Safari quirks)
  useEffect(() => {
    const v = heroVideoRef.current;
    if (!v) return;
    // Set mute BEFORE any source/load to satisfy iOS Safari autoplay policy
    v.muted = true;
    v.defaultMuted = true;
    v.setAttribute('muted', '');
    v.setAttribute('playsinline', '');
    v.setAttribute('webkit-playsinline', '');
    v.setAttribute('autoplay', '');
    v.playsInline = true;

    let cancelled = false;
    const tryPlay = () => {
      if (cancelled || !v) return;
      v.muted = true; // re-assert before each attempt (iOS resets on some events)
      const p = v.play();
      if (p && typeof p.catch === 'function') {
        p.catch(() => { /* retry handlers below will pick this up */ });
      }
    };

    // 1. Try immediately
    tryPlay();
    // 2. Force a load + try when metadata/data arrives
    try { v.load(); } catch (_) {}
    v.addEventListener('loadedmetadata', tryPlay);
    v.addEventListener('loadeddata', tryPlay);
    v.addEventListener('canplay', tryPlay);
    v.addEventListener('canplaythrough', tryPlay);
    // 3. Retry whenever the page becomes visible (covers tab-switch / lock-screen)
    const onVis = () => { if (document.visibilityState === 'visible') tryPlay(); };
    document.addEventListener('visibilitychange', onVis);
    // 4. Last resort: try on the very first user interaction anywhere
    const onInteract = () => tryPlay();
    document.addEventListener('touchstart', onInteract, { passive: true });
    document.addEventListener('touchend', onInteract, { passive: true });
    document.addEventListener('pointerdown', onInteract);
    document.addEventListener('scroll', onInteract, { passive: true });

    return () => {
      cancelled = true;
      v.removeEventListener('loadedmetadata', tryPlay);
      v.removeEventListener('loadeddata', tryPlay);
      v.removeEventListener('canplay', tryPlay);
      v.removeEventListener('canplaythrough', tryPlay);
      document.removeEventListener('visibilitychange', onVis);
      document.removeEventListener('touchstart', onInteract);
      document.removeEventListener('touchend', onInteract);
      document.removeEventListener('pointerdown', onInteract);
      document.removeEventListener('scroll', onInteract);
    };
  }, []);

  // Parallax mouse effect
  useEffect(() => {
    const handleMouseMove = (e) => {
      setMousePosition({
        x: (e.clientX / window.innerWidth - 0.5) * 20,
        y: (e.clientY / window.innerHeight - 0.5) * 20
      });
    };
    window.addEventListener('mousemove', handleMouseMove);
    return () => window.removeEventListener('mousemove', handleMouseMove);
  }, []);

  const ratingCounter = useCounter(5, 1500);
  const qualityCounter = useCounter(100, 2000);
  const toleranceCounter = useCounter(1, 1000);

  const validateForm = () => {
    const newErrors = {};
    if (!formData.name.trim() || formData.name.length < 2) newErrors.name = "Name must be at least 2 characters";
    if (!formData.email.trim()) newErrors.email = "Email is required";
    else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) newErrors.email = "Please enter a valid email";
    if (!formData.message.trim() || formData.message.length < 10) newErrors.message = "Message must be at least 10 characters";
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validateForm()) { toast.error("Please fix the errors in the form"); return; }
    setIsSubmitting(true);
    try {
      await axios.post(`${API}/contact`, formData);
      setIsSubmitted(true);
      toast.success("Enquiry sent successfully! We'll be in touch soon.");
      setFormData({ name: "", email: "", phone: "", company: "", service_interest: "", message: "" });
    } catch (error) {
      toast.error("Failed to send enquiry. Please try again or call us directly.");
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    if (errors[name]) setErrors(prev => ({ ...prev, [name]: "" }));
  };

  const scrollToContact = (e) => {
    e.preventDefault();
    document.querySelector('#contact')?.scrollIntoView({ behavior: 'smooth' });
  };

  return (
    <div data-testid="home-page" className="grain">
      {/* Hero Section */}
      <section 
        id="home"
        className="relative min-h-screen flex items-end justify-center overflow-hidden bg-[#0a1628]"
        data-testid="hero-section"
      >
        {/* Video Background */}
        <div className="absolute inset-0">
          <video
            ref={heroVideoRef}
            autoPlay
            muted
            loop
            playsInline
            preload="auto"
            src="/videos/cnc-hero.mp4"
            className="absolute inset-0 w-full h-full object-cover pointer-events-none"
            poster="/images/hero-poster.webp"
          />
          
          {/* Gradient overlay - lighter so the machine is visible */}
          <div className="absolute inset-0 bg-gradient-to-br from-[#0a1628]/75 via-[#0F2557]/55 to-[#0a1628]/80" />
          
          {/* Subtle vignette for text legibility */}
          <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_center,_transparent_0%,_rgba(10,22,40,0.55)_100%)]" />
          
          {/* Animated scan line */}
          <div className="absolute inset-0 overflow-hidden pointer-events-none">
            <div className="absolute w-full h-[2px] bg-gradient-to-r from-transparent via-cyan-400/30 to-transparent animate-[scanLine_4s_linear_infinite]" />
          </div>
          
          {/* Grid overlay */}
          <div className="absolute inset-0 opacity-[0.03]" style={{
            backgroundImage: `linear-gradient(rgba(255,255,255,.1) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,.1) 1px, transparent 1px)`,
            backgroundSize: '60px 60px'
          }} />
          
          {/* Animated circles/gears */}
          <div className="absolute top-20 left-20 w-40 h-40 border border-cyan-500/20 rounded-full animate-[spin_20s_linear_infinite]">
            <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-32 h-32 border border-cyan-500/20 rounded-full" />
            <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-24 h-24 border border-cyan-500/30 rounded-full animate-[spin_10s_linear_infinite_reverse]" />
          </div>
          
          <div className="absolute bottom-32 right-20 w-56 h-56 border border-blue-500/10 rounded-full animate-[spin_30s_linear_infinite_reverse]">
            <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-44 h-44 border border-blue-500/10 rounded-full" />
          </div>
        </div>
        
        <div className="relative mx-auto max-w-5xl px-4 sm:px-6 lg:px-8 pt-24 sm:pt-32 pb-32 sm:pb-40 text-center z-10">
          <div className="space-y-5 sm:space-y-6">
            {/* Badge */}
            <div className="inline-flex items-center gap-2 sm:gap-3 px-3 sm:px-4 py-1.5 sm:py-2 bg-white/5 backdrop-blur-md border border-white/10 rounded-full animate-[heroReveal_0.8s_ease_forwards]" style={{ animationDelay: '0.1s', opacity: 0 }}>
              <div className="relative">
                <div className="w-1.5 h-1.5 sm:w-2 sm:h-2 bg-emerald-400 rounded-full" />
                <div className="absolute inset-0 w-1.5 h-1.5 sm:w-2 sm:h-2 bg-emerald-400 rounded-full animate-ping" />
              </div>
              <span className="text-[9px] sm:text-[11px] tracking-[0.15em] uppercase font-medium text-white/80 whitespace-nowrap">
                <span className="sm:hidden">Precision Engineering · Hull</span>
                <span className="hidden sm:inline">Sub-Contract Precision Engineering · Hull</span>
              </span>
            </div>
            
            {/* Main Heading */}
            <div className="space-y-0.5 sm:space-y-1">
              <h1 className="text-[2.5rem] leading-[1.05] sm:text-5xl lg:text-6xl xl:text-7xl tracking-tight font-black text-white animate-[heroReveal_0.8s_ease_forwards]" style={{ animationDelay: '0.2s', opacity: 0 }}>
                Precision Engineering
              </h1>
              <h1 className="text-[2.5rem] leading-[1.05] sm:text-5xl lg:text-6xl xl:text-7xl tracking-tight font-black animate-[heroReveal_0.8s_ease_forwards]" style={{ animationDelay: '0.3s', opacity: 0 }}>
                <span className="relative inline-block">
                  <span className="text-transparent bg-clip-text bg-gradient-to-r from-cyan-400 via-blue-400 to-cyan-400 bg-[length:200%_auto] animate-[gradientMove_3s_linear_infinite]">&amp; CNC Machining</span>
                </span>
              </h1>
            </div>
            
            {/* Animated underline */}
            <div className="flex justify-center animate-[heroReveal_0.8s_ease_forwards]" style={{ animationDelay: '0.35s', opacity: 0 }}>
              <div className="w-16 sm:w-20 h-[2px] bg-gradient-to-r from-transparent via-cyan-400 to-transparent" />
            </div>
            
            {/* Subtitle */}
            <p className="text-sm sm:text-lg text-white/65 max-w-xs sm:max-w-xl mx-auto leading-relaxed animate-[heroReveal_0.8s_ease_forwards]" style={{ animationDelay: '0.4s', opacity: 0 }}>
              Bespoke machined components produced to exacting measurements. From a single prototype to large volume batches — supplied on time, every time.
            </p>
            
            {/* CTA Buttons */}
            <div className="flex flex-col sm:flex-row justify-center gap-3 sm:gap-4 pt-2 sm:pt-4 animate-[heroReveal_0.8s_ease_forwards]" style={{ animationDelay: '0.5s', opacity: 0 }}>
              <Button 
                size="lg"
                onClick={scrollToContact}
                className="group relative bg-gradient-to-r from-cyan-500 to-blue-500 text-white hover:from-cyan-400 hover:to-blue-400 rounded-full px-7 sm:px-8 py-5 sm:py-6 text-sm font-bold overflow-hidden transition-all duration-300 hover:shadow-[0_0_40px_rgba(34,211,238,0.4)] hover:scale-105"
                data-testid="hero-contact-us"
              >
                <span className="relative z-10 flex items-center justify-center">
                  Get a Free Quote
                  <ArrowRight className="ml-2 h-4 w-4 transition-transform group-hover:translate-x-1" />
                </span>
              </Button>
              <Button 
                size="lg"
                variant="outline"
                className="group border border-white/20 text-white hover:bg-white/10 hover:border-cyan-400/50 rounded-full px-7 sm:px-8 py-5 sm:py-6 text-sm font-bold transition-all duration-300 hover:shadow-[0_0_30px_rgba(34,211,238,0.2)]"
                data-testid="hero-call-now"
                asChild
              >
                <a href="tel:01482221098">
                  <Phone className="mr-2 h-4 w-4" />
                  01482 221098
                </a>
              </Button>
            </div>
            
            {/* Trust Indicators */}
            <div className="flex flex-wrap items-center justify-center gap-2 sm:gap-6 pt-4 sm:pt-8 animate-[heroReveal_0.8s_ease_forwards]" style={{ animationDelay: '0.6s', opacity: 0 }}>
              <div className="flex items-center gap-1.5 sm:gap-2 px-3 sm:px-4 py-1.5 sm:py-2 bg-white/5 backdrop-blur-sm rounded-full border border-white/10">
                <div className="flex">
                  {[...Array(5)].map((_, i) => (
                    <Star key={i} className="h-3 w-3 sm:h-4 sm:w-4 fill-yellow-400 text-yellow-400" />
                  ))}
                </div>
                <span className="text-white/70 text-xs sm:text-sm font-medium">5.0</span>
              </div>
              <div className="flex items-center gap-1.5 sm:gap-2 px-3 sm:px-4 py-1.5 sm:py-2 bg-white/5 backdrop-blur-sm rounded-full border border-white/10">
                <CheckCircle2 className="h-3 w-3 sm:h-4 sm:w-4 text-emerald-400" />
                <span className="text-white/70 text-xs sm:text-sm">ISO Standards</span>
              </div>
              <div className="flex items-center gap-1.5 sm:gap-2 px-3 sm:px-4 py-1.5 sm:py-2 bg-white/5 backdrop-blur-sm rounded-full border border-white/10">
                <MapPin className="h-3 w-3 sm:h-4 sm:w-4 text-cyan-400" />
                <span className="text-white/70 text-xs sm:text-sm">Hull, UK</span>
              </div>
            </div>
          </div>
        </div>

        {/* Scroll indicator */}
        <div className="absolute bottom-8 left-1/2 -translate-x-1/2 flex flex-col items-center gap-3 z-10">
          <span className="text-white/40 text-xs tracking-[0.2em] uppercase">Explore</span>
          <div className="w-6 h-10 border-2 border-white/20 rounded-full flex justify-center pt-2">
            <div className="w-1 h-2 bg-cyan-400 rounded-full animate-[scrollBounce_1.5s_ease-in-out_infinite]" />
          </div>
        </div>
        
        {/* Corner accents */}
        <div className="absolute top-0 left-0 w-32 h-32 border-l-2 border-t-2 border-cyan-500/20" />
        <div className="absolute bottom-0 right-0 w-32 h-32 border-r-2 border-b-2 border-cyan-500/20" />
      </section>

      {/* Stats Bar */}
      <section className="py-0 bg-[#0F2557] relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-r from-[#0F2557] via-[#1a3a7a] to-[#0F2557]" />
        <div className="relative mx-auto max-w-7xl">
          <div className="grid grid-cols-3 divide-x divide-white/10">
            <div ref={qualityCounter.ref} className="py-10 text-center">
              <p className="text-5xl lg:text-6xl font-black text-white mb-2">{qualityCounter.count}+</p>
              <p className="text-white/60 text-sm tracking-wide uppercase">Years Combined Experience</p>
            </div>
            <div ref={ratingCounter.ref} className="py-10 text-center">
              <p className="text-5xl lg:text-6xl font-black text-white mb-2">{ratingCounter.count}.0</p>
              <p className="text-white/60 text-sm tracking-wide uppercase">Google Rating</p>
            </div>
            <div ref={toleranceCounter.ref} className="py-10 text-center">
              <p className="text-5xl lg:text-6xl font-black text-white mb-2">±0.0{toleranceCounter.count}mm</p>
              <p className="text-white/60 text-sm tracking-wide uppercase">Precision Tolerance</p>
            </div>
          </div>
        </div>
      </section>

      {/* About Section */}
      <section id="about" className="py-28 lg:py-40 bg-white relative morph-bg" data-testid="about-section">
        <div className="relative mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-16 lg:gap-24 items-center">
            <div className="reveal-left">
              <p className="text-xs tracking-[0.3em] uppercase font-semibold text-[#0F2557] mb-6 flex items-center gap-2">
                <span className="w-12 h-px bg-[#0F2557]" />
                About Fox Precision
              </p>
              <h2 className="text-4xl sm:text-5xl lg:text-6xl tracking-tight font-black text-[#1A1D20] mb-8 leading-[1.1]">
                A Hull <span className="text-gradient">Family Business</span> Built On Precision
              </h2>
              <p className="text-lg text-[#6B7280] leading-relaxed mb-6">
                As a leading sub-contract precision engineering company, Fox Precision Engineering Ltd offers high-quality solutions from <span className="font-semibold text-[#1A1D20]">initial concept through to production</span>. We work closely with every customer to produce their exact requirements — bespoke components in a wide range of specifications and materials.
              </p>
              <p className="text-lg text-[#6B7280] leading-relaxed mb-6">
                Based in Hull, East Yorkshire, we have built an <span className="font-semibold text-[#1A1D20]">extensive customer base both locally and nationally</span>, working across valves and conveyors, hydraulics, packaging and machinery. These sectors form our core business — but with our in-house facilities we have the capability to produce machined components for any industry sector.
              </p>
              <p className="text-lg text-[#6B7280] leading-relaxed mb-6">
                Our enviable reputation has been built on <span className="font-semibold text-[#1A1D20]">quality, customer service and competitive pricing</span>. We continually invest in high-specification machines and equipment — and in our people — to provide cutting-edge precision engineering solutions for the long term.
              </p>
              <p className="text-lg text-[#6B7280] leading-relaxed mb-10">
                Past winners of <span className="font-semibold text-[#0F2557]">HTAE Employer Of The Year 2017</span>, we now proudly sponsor the same award. We are committed to apprenticeship training and the community — supporting many business and sporting events across the region.
              </p>
              <div className="grid grid-cols-2 gap-8">
                <div className="border-l-4 border-[#0F2557] pl-6 py-2">
                  <p className="text-4xl font-black text-[#0F2557]">Ø375mm</p>
                  <p className="text-sm text-[#6B7280] mt-1">Max Turning Diameter</p>
                </div>
                <div className="border-l-4 border-[#0F2557] pl-6 py-2">
                  <p className="text-4xl font-black text-[#0F2557]">900 × 550</p>
                  <p className="text-sm text-[#6B7280] mt-1">Max Mill Envelope (mm)</p>
                </div>
              </div>
            </div>
            <div className="reveal-right">
              <div className="relative">
                <div className="relative overflow-hidden">
                  <img
                    src="/images/about-us.webp"
                    alt="Fox Precision Engineering — XYZ CNC machining centre on the shop floor in Hull"
                    width="800"
                    height="618"
                    loading="lazy"
                    decoding="async"
                    className="w-full h-[500px] lg:h-[600px] object-cover"
                  />
                  
                  {/* Bottom gradient for legibility */}
                  <div className="absolute inset-x-0 bottom-0 h-44 bg-gradient-to-t from-black/85 via-black/40 to-transparent pointer-events-none" />
                  
                  {/* Integrated location bar */}
                  <div className="absolute inset-x-0 bottom-0 p-6 sm:p-8">
                    <div className="flex items-end justify-between gap-6">
                      <div>
                        <div className="flex items-center gap-2 mb-2">
                          <div className="w-1.5 h-1.5 bg-cyan-400 rounded-full" />
                          <p className="text-[10px] tracking-[0.3em] uppercase text-cyan-400 font-medium">Based In</p>
                        </div>
                        <p className="text-4xl sm:text-5xl font-black text-white leading-none mb-2">Hull, UK</p>
                        <p className="text-white/70 text-sm font-light">82–84 York Street &middot; HU2 0QD</p>
                      </div>
                      <a
                        href="https://maps.google.com/?q=82-84+York+Street+Hull+HU2+0QD"
                        target="_blank"
                        rel="noopener noreferrer"
                        className="hidden sm:flex items-center gap-2 text-white/80 hover:text-white text-xs tracking-[0.2em] uppercase font-medium border-b border-white/30 hover:border-white pb-1 transition-all"
                        data-testid="about-directions-link"
                      >
                        Directions
                        <ArrowRight className="h-3 w-3" />
                      </a>
                    </div>
                  </div>
                </div>
                
                {/* Floating corner accent — coordinates style */}
                <div className="absolute top-6 right-6 bg-white/95 backdrop-blur-md px-4 py-2.5 shadow-lg">
                  <p className="text-[9px] tracking-[0.25em] uppercase text-[#6B7280] font-medium">53.7456° N</p>
                  <p className="text-[9px] tracking-[0.25em] uppercase text-[#6B7280] font-medium">0.3367° W</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Why Choose Us */}
      <section className="py-28 lg:py-40 bg-[#FAFAFA]" data-testid="why-choose-section">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-16 lg:gap-24 items-center">
            <div className="order-2 lg:order-1 reveal-left">
              <div className="img-premium">
                <img
                  src="/images/why-choose.webp"
                  alt="CNC Machine in Operation"
                  width="1400"
                  height="933"
                  loading="lazy"
                  decoding="async"
                  className="w-full h-[500px] lg:h-[600px] object-cover"
                />
              </div>
            </div>
            <div className="order-1 lg:order-2 reveal-right">
              <p className="text-xs tracking-[0.3em] uppercase font-semibold text-[#0F2557] mb-6 flex items-center gap-2">
                <span className="w-12 h-px bg-[#0F2557]" />
                Why Choose Us
              </p>
              <h2 className="text-4xl sm:text-5xl lg:text-6xl tracking-tight font-black text-[#1A1D20] mb-10 leading-[1.1]">
                Your Partner in <span className="text-gradient">Precision</span>
              </h2>
              
              <div className="space-y-8 stagger-children">
                {whyChooseUs.map((item, index) => (
                  <div key={item.title} className="flex gap-5 group" data-testid={`why-choose-${index}`}>
                    <div className="flex-shrink-0 w-12 h-12 bg-[#0F2557] flex items-center justify-center transition-all duration-300 group-hover:scale-110 group-hover:rotate-3">
                      <CheckCircle2 className="h-6 w-6 text-white" />
                    </div>
                    <div>
                      <h3 className="text-xl font-bold text-[#1A1D20] mb-2">{item.title}</h3>
                      <p className="text-[#6B7280]">{item.description}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Services Section */}
      <section id="services" className="py-28 lg:py-40 bg-white" data-testid="services-section">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-20 reveal">
            <p className="text-xs tracking-[0.3em] uppercase font-semibold text-[#0F2557] mb-6">Our Services</p>
            <h2 className="text-4xl sm:text-5xl lg:text-6xl tracking-tight font-black text-[#1A1D20] leading-[1.1]">
              Comprehensive Engineering <span className="text-gradient">Solutions</span>
            </h2>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8 stagger-children">
            {services.map((service, index) => (
              <div 
                key={service.title}
                className="card-premium p-10 bg-white border border-[#E5E7EB]"
                data-testid={`service-card-${index}`}
              >
                <service.icon className="card-icon h-14 w-14 text-[#6B7280] mb-6" strokeWidth={1} />
                <h3 className="text-2xl font-bold text-[#1A1D20] mb-4">{service.title}</h3>
                <p className="text-[#6B7280] leading-relaxed">{service.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Products Section */}
      <section id="products" className="py-28 lg:py-40 bg-[#FAFAFA] relative overflow-hidden" data-testid="products-section">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16 reveal">
            <p className="text-xs tracking-[0.3em] uppercase font-semibold text-[#0F2557] mb-6">Our Products</p>
            <h2 className="text-4xl sm:text-5xl lg:text-6xl tracking-tight font-black text-[#1A1D20] leading-[1.1] mb-6">
              Bespoke Machined <span className="text-gradient">Components</span>
            </h2>
            <p className="text-lg text-[#6B7280] max-w-3xl mx-auto leading-relaxed">
              Producing bespoke machined components to exacting tolerances at competitive prices — backed by a wealth of experience, skill and expertise. With over 100 years of experience collectively amongst our workforce, we have the capabilities to handle and machine even the most challenging products or projects.
            </p>
          </div>

          {/* Product Gallery */}
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4 lg:gap-6 mb-20 stagger-children">
            {[1,2,3,4,5,6,7,8].map((n) => {
              const id = String(n).padStart(3, '0');
              return (
                <div 
                  key={id}
                  className="group relative aspect-square overflow-hidden bg-white border border-[#E5E7EB] transition-all duration-500 hover:border-[#0F2557]/40 hover:shadow-xl"
                  data-testid={`product-${id}`}
                >
                  <img 
                    src={`/products/${id}.webp`}
                    alt={`Fox Precision Engineering machined component ${n}`}
                    width="900"
                    height="900"
                    loading="lazy"
                    decoding="async"
                    className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-[#0F2557]/80 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500" />
                  <div className="absolute bottom-0 left-0 right-0 p-4 text-white translate-y-4 opacity-0 group-hover:translate-y-0 group-hover:opacity-100 transition-all duration-500">
                    <p className="text-xs tracking-[0.2em] uppercase font-semibold">Precision Machined</p>
                  </div>
                </div>
              );
            })}
          </div>

          {/* Products & Post-Production Treatments */}
          <div className="grid lg:grid-cols-2 gap-12 lg:gap-16 mb-20 stagger-children">
            <div className="reveal-left bg-white p-10 lg:p-12 border border-[#E5E7EB]">
              <div className="flex items-center gap-4 mb-8">
                <div className="w-12 h-12 bg-[#0F2557] flex items-center justify-center">
                  <Layers className="h-6 w-6 text-white" strokeWidth={1.5} />
                </div>
                <h3 className="text-2xl font-black text-[#1A1D20]">Products & Post-Production Treatments</h3>
              </div>
              <p className="text-[#6B7280] mb-8 leading-relaxed">An indication of the product services and production treatments we can provide:</p>
              <ul className="space-y-3">
                {[
                  "CNC Milling",
                  "CNC Turning",
                  "Manual / Semi-Automatic Milling and Turning",
                  "Manual and CNC Grinding",
                  "Product Assemblies",
                  "Plating and Heat Treatments",
                  "Anodising",
                  "Painting and Final Special Finishing",
                  "Coded Welding and Assembly",
                  "Internal and External Grinding",
                  "All Keywaying and Slotting needs",
                ].map((item) => (
                  <li key={item} className="flex items-start gap-3">
                    <CheckCircle2 className="h-5 w-5 text-[#0F2557] flex-shrink-0 mt-0.5" />
                    <span className="text-[#1A1D20]">{item}</span>
                  </li>
                ))}
              </ul>
            </div>

            <div className="space-y-6">
              <div className="reveal-right bg-[#0F2557] p-10 lg:p-12 text-white">
                <div className="flex items-center gap-4 mb-6">
                  <div className="w-12 h-12 bg-white/10 backdrop-blur flex items-center justify-center">
                    <Wrench className="h-6 w-6 text-white" strokeWidth={1.5} />
                  </div>
                  <h3 className="text-2xl font-black">Prototype Service</h3>
                </div>
                <p className="text-white/80 leading-relaxed mb-4">
                  To ensure we fulfil your exact requirements, we produce prototypes so that machined components are of a consistently high standard before full production is progressed.
                </p>
                <p className="text-white/80 leading-relaxed">
                  Our <span className="font-semibold text-white">rapid prototype service</span> ensures projects can be progressed without undue delays. We also produce one-off prototypes for research & development projects.
                </p>
              </div>

              <div className="reveal-right bg-white p-10 lg:p-12 border border-[#E5E7EB]">
                <div className="flex items-center gap-4 mb-6">
                  <div className="w-12 h-12 bg-[#0F2557] flex items-center justify-center">
                    <Truck className="h-6 w-6 text-white" strokeWidth={1.5} />
                  </div>
                  <h3 className="text-2xl font-black text-[#1A1D20]">Delivery Service</h3>
                </div>
                <p className="text-[#6B7280] leading-relaxed">
                  We offer a full delivery service to our customers. Working to demanding deadlines, we ensure your products are delivered to you — <span className="font-semibold text-[#1A1D20]">on time, every time</span>.
                </p>
              </div>
            </div>
          </div>

          {/* CTA */}
          <div className="reveal text-center bg-[#0F2557] p-12 lg:p-16 text-white">
            <h3 className="text-3xl sm:text-4xl font-black mb-6">Choose Fox Precision Engineering For All Your Precision Machined Components</h3>
            <p className="text-white/70 mb-8 max-w-2xl mx-auto">Call us today or send us your drawings for a free, no-obligation quote.</p>
            <div className="flex flex-col sm:flex-row justify-center gap-4">
              <Button
                size="lg"
                onClick={scrollToContact}
                className="bg-white text-[#0F2557] hover:bg-cyan-100 rounded-full px-8 py-6 text-sm font-bold"
                data-testid="products-cta-quote"
              >
                Request A Quote
                <ArrowRight className="ml-2 h-4 w-4" />
              </Button>
              <Button
                size="lg"
                variant="outline"
                className="border-white/30 text-white hover:bg-white/10 rounded-full px-8 py-6 text-sm font-bold"
                asChild
              >
                <a href="tel:01482221098" data-testid="products-cta-call">
                  <Phone className="mr-2 h-4 w-4" />
                  01482 221098
                </a>
              </Button>
            </div>
          </div>
        </div>
      </section>

      {/* Machinery Section */}
      <section id="machinery" className="py-28 lg:py-40 bg-[#0F2557] relative overflow-hidden" data-testid="machinery-section">
        <div className="absolute inset-0 opacity-10">
          <div className="absolute inset-0" style={{
            backgroundImage: `url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%23ffffff' fill-opacity='0.4'%3E%3Cpath d='M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")`
          }} />
        </div>
        
        <div className="relative mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16 reveal">
            <p className="text-xs tracking-[0.3em] uppercase font-semibold text-white/60 mb-6">Our Capabilities</p>
            <h2 className="text-4xl sm:text-5xl lg:text-6xl tracking-tight font-black text-white leading-[1.1] mb-6">
              In-House Facilities
            </h2>
            <p className="text-white/70 text-lg max-w-3xl mx-auto">
              Continuous investment in the latest machinery and technology ensures we machine complex parts to exacting measurements, within tight deadlines and to your budget.
            </p>
          </div>

          {/* Featured Machine Gallery */}
          <div className="reveal mb-16">
            <div className="grid md:grid-cols-3 gap-6 stagger-children">
              {[
                { 
                  img: "/images/c1.webp",
                  name: "XYZ Mini Mill 560",
                  type: "Vertical Milling Machine",
                  spec: "Siemens CNC Control"
                },
                { 
                  img: "/images/c2.webp",
                  name: "XYZ 1010 VMC",
                  type: "Vertical Machining Centre",
                  spec: "1000mm × 500mm Table"
                },
                { 
                  img: "/images/c3.webp",
                  name: "XYZ 3000 Turret Mill",
                  type: "Manual Turret Mill",
                  spec: "DRO Equipped"
                },
              ].map((machine, index) => (
                <div 
                  key={machine.name}
                  className="group relative bg-white/[0.03] backdrop-blur border border-white/10 overflow-hidden transition-all duration-500 hover:border-cyan-400/40 hover:bg-white/[0.06]"
                  data-testid={`featured-machine-${index}`}
                >
                  <div className="aspect-[4/5] overflow-hidden bg-white">
                    <img 
                      src={machine.img}
                      alt={machine.name}
                      width="800"
                      height="618"
                      loading="lazy"
                      decoding="async"
                      className="w-full h-full object-contain p-6 transition-transform duration-700 group-hover:scale-105"
                    />
                  </div>
                  <div className="p-6 border-t border-white/10">
                    <p className="text-xs tracking-[0.2em] uppercase text-cyan-400/70 mb-2">{machine.spec}</p>
                    <h4 className="text-xl font-bold text-white mb-1">{machine.name}</h4>
                    <p className="text-white/50 text-sm">{machine.type}</p>
                  </div>
                  <div className="absolute top-4 right-4 w-10 h-10 bg-[#0F2557]/80 backdrop-blur border border-white/20 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                    <Cog className="h-5 w-5 text-white" strokeWidth={1.5} />
                  </div>
                </div>
              ))}
            </div>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6 mb-16 stagger-children">
            {[
              { icon: Gauge, title: "High Precision", desc: "±0.01mm tolerances" },
              { icon: Cog, title: "Complex Parts", desc: "Multi-axis machining" },
              { icon: Layers, title: "Various Materials", desc: "Metals & plastics" },
              { icon: Ruler, title: "Quality Assured", desc: "Full inspection" },
            ].map((feature, index) => (
              <div 
                key={feature.title}
                className="p-8 bg-white/5 backdrop-blur border border-white/10 text-center transition-all duration-500 hover:bg-white/10 hover:scale-105 hover:border-white/30"
                data-testid={`feature-${index}`}
              >
                <feature.icon className="h-12 w-12 text-white/80 mx-auto mb-5 transition-transform duration-500 hover:rotate-12" strokeWidth={1} />
                <h3 className="text-xl font-bold text-white mb-2">{feature.title}</h3>
                <p className="text-white/60">{feature.desc}</p>
              </div>
            ))}
          </div>

          {/* Equipment List */}
          <div className="reveal mb-16">
            <h3 className="text-2xl font-bold text-white mb-8 text-center">Our Equipment</h3>
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4 stagger-children">
              {[
                { name: "Yang CK 250", type: "Dia Chucking Lathe" },
                { name: "Yang ML 300", type: "Dia Chucking Lathe" },
                { name: "Dugard Eagle 200", type: "Bar Fed Lathe (6-50mm dia)" },
                { name: "FEMCO Bar Fed Lathe", type: "Bar Fed Lathe (6-40mm dia)" },
                { name: "XYZ Mini Mill 560", type: "Milling Machine" },
                { name: "XYZ VMC 1010", type: "Vertical Machining Centre" },
                { name: "Dugard ECO 1000 VMC", type: "Vertical Machining Centre" },
                { name: "Colchester CNC 2000", type: "CNC Lathe" },
                { name: "3x Centre Lathes", type: "Manual Lathes" },
                { name: "KRV Turret Mill", type: "Turret Mill" },
                { name: "Bridgeport Turret Mill", type: "Turret Mill" },
                { name: "2x Surface Grinders", type: "Up to 400mm x 200mm" },
                { name: "Butler 10\" Slotter", type: "Slotting Machine" },
              ].map((equipment, index) => (
                <div 
                  key={index}
                  className="p-5 bg-white/5 backdrop-blur border border-white/10 transition-all duration-300 hover:bg-white/10 hover:border-white/30 flex items-center gap-4"
                >
                  <div className="w-10 h-10 bg-white/10 flex items-center justify-center flex-shrink-0">
                    <Cog className="h-5 w-5 text-white/70" />
                  </div>
                  <div>
                    <p className="text-white font-semibold">{equipment.name}</p>
                    <p className="text-white/50 text-sm">{equipment.type}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>

          <div className="reveal">
            <h3 className="text-2xl font-bold text-white mb-10 text-center">Materials We Work With</h3>
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-4 stagger-children">
              {materials.map((material, index) => (
                <div 
                  key={material.name}
                  className="p-6 bg-white/5 backdrop-blur border border-white/10 text-center transition-all duration-300 hover:bg-white hover:text-[#0F2557] group cursor-pointer"
                  data-testid={`material-${index}`}
                >
                  <p className="text-white font-bold group-hover:text-[#0F2557] transition-colors">{material.name}</p>
                  <p className="text-white/50 text-xs mt-1 group-hover:text-[#6B7280] transition-colors">{material.description}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Industries Served */}
      <section className="py-28 lg:py-40 bg-[#FAFAFA]" data-testid="industries-section">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-20 reveal">
            <p className="text-xs tracking-[0.3em] uppercase font-semibold text-[#0F2557] mb-6">Industries We Serve</p>
            <h2 className="text-4xl sm:text-5xl lg:text-6xl tracking-tight font-black text-[#1A1D20] leading-[1.1]">
              Trusted Across <span className="text-gradient">Industries</span>
            </h2>
          </div>

          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-6 stagger-children">
            {industries.map((industry, index) => (
              <div 
                key={industry.name}
                className="card-premium p-8 bg-white border border-[#E5E7EB] text-center"
                data-testid={`industry-${index}`}
              >
                <industry.icon className="card-icon h-12 w-12 text-[#6B7280] mx-auto mb-4" strokeWidth={1} />
                <p className="text-[#1A1D20] font-semibold">{industry.name}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Award / Recognition */}
      <section className="py-28 lg:py-40 bg-white relative overflow-hidden" data-testid="award-section">
        <div className="absolute inset-0 flex items-center justify-center opacity-5">
          <Award className="w-[400px] h-[400px] text-[#0F2557]" strokeWidth={1} />
        </div>
        
        <div className="relative mx-auto max-w-5xl px-4 sm:px-6 lg:px-8 reveal-scale">
          <div className="text-center">
            <div className="inline-flex items-center justify-center w-20 h-20 bg-[#0F2557] mb-10">
              <Award className="h-10 w-10 text-white" strokeWidth={1.5} />
            </div>
            <p className="text-xs tracking-[0.3em] uppercase font-semibold text-[#0F2557] mb-6">Recognised Excellence</p>
            <p className="text-3xl sm:text-4xl lg:text-5xl font-medium text-[#1A1D20] mb-10 leading-tight">
              HTAE <span className="text-gradient">Employer Of The Year</span> 2017 — and proud sponsors of the same award today.
            </p>
            <p className="text-lg text-[#6B7280] max-w-3xl mx-auto leading-relaxed mb-12">
              We're committed to apprenticeship training and the wider community — providing sponsorship to many business and sporting events across Hull and East Yorkshire. Investment in our people is investment in your parts.
            </p>
            <div className="grid sm:grid-cols-3 gap-8 max-w-3xl mx-auto pt-8 border-t border-[#E5E7EB]">
              <div>
                <p className="text-3xl font-black text-[#0F2557] mb-2">2017</p>
                <p className="text-sm text-[#6B7280] uppercase tracking-wider">HTAE Award Winners</p>
              </div>
              <div>
                <p className="text-3xl font-black text-[#0F2557] mb-2">Active</p>
                <p className="text-sm text-[#6B7280] uppercase tracking-wider">Award Sponsors</p>
              </div>
              <div>
                <p className="text-3xl font-black text-[#0F2557] mb-2">Apprentices</p>
                <p className="text-sm text-[#6B7280] uppercase tracking-wider">Training Committed</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Contact Section */}
      <section id="contact" className="py-28 lg:py-40 bg-[#FAFAFA]" data-testid="contact-section">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-20 reveal">
            <p className="text-xs tracking-[0.3em] uppercase font-semibold text-[#0F2557] mb-6">Contact Us</p>
            <h2 className="text-4xl sm:text-5xl lg:text-6xl tracking-tight font-black text-[#1A1D20] leading-[1.1]">
              Get in <span className="text-gradient">Touch</span>
            </h2>
            <p className="text-lg text-[#6B7280] mt-6 max-w-2xl mx-auto">
              Ready to discuss your precision engineering requirements? Contact us for a free quote.
            </p>
          </div>

          {/* Contact Info Cards */}
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6 mb-20 stagger-children">
            <div className="card-premium p-10 bg-white border border-[#E5E7EB] text-center">
              <MapPin className="card-icon h-10 w-10 text-[#0F2557] mx-auto mb-5" />
              <h3 className="text-xl font-bold text-[#1A1D20] mb-3">Address</h3>
              <p className="text-[#6B7280]">82–84 York Street<br />Hull HU2 0QD<br />East Yorkshire</p>
            </div>
            <div className="card-premium p-10 bg-white border border-[#E5E7EB] text-center">
              <Phone className="card-icon h-10 w-10 text-[#0F2557] mx-auto mb-5" />
              <h3 className="text-xl font-bold text-[#1A1D20] mb-3">Phone</h3>
              <a href="tel:01482221098" className="text-[#0F2557] font-bold text-lg underline-animate" data-testid="contact-phone">
                01482 221098
              </a>
            </div>
            <div className="card-premium p-10 bg-white border border-[#E5E7EB] text-center">
              <Send className="card-icon h-10 w-10 text-[#0F2557] mx-auto mb-5" />
              <h3 className="text-xl font-bold text-[#1A1D20] mb-3">Email</h3>
              <a href="mailto:enquiries@fox-precision-eng.co.uk" className="text-[#0F2557] font-bold text-sm underline-animate break-all" data-testid="contact-email">
                enquiries@fox-precision-eng.co.uk
              </a>
            </div>
            <div className="card-premium p-10 bg-white border border-[#E5E7EB] text-center">
              <Clock className="card-icon h-10 w-10 text-[#0F2557] mx-auto mb-5" />
              <h3 className="text-xl font-bold text-[#1A1D20] mb-3">Hours</h3>
              <p className="text-[#6B7280]">Mon – Fri<br />Open until 5pm</p>
            </div>
          </div>

          <div className="grid lg:grid-cols-2 gap-12 lg:gap-16">
            {/* Contact Form */}
            <div className="reveal-left">
              <div className="bg-white p-10 border border-[#E5E7EB] shadow-xl">
                <h3 className="text-2xl font-bold text-[#1A1D20] mb-8">Send Us an Enquiry</h3>

                {isSubmitted ? (
                  <div className="text-center py-12" data-testid="form-success">
                    <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6">
                      <CheckCircle2 className="h-10 w-10 text-green-600" />
                    </div>
                    <h4 className="text-2xl font-bold text-[#1A1D20] mb-3">Enquiry Sent!</h4>
                    <p className="text-[#6B7280] mb-8">Thank you for your enquiry. We'll be in touch shortly.</p>
                    <Button onClick={() => setIsSubmitted(false)} className="btn-premium bg-[#0F2557] text-white rounded-none px-8 py-6">
                      Send Another Enquiry
                    </Button>
                  </div>
                ) : (
                  <form onSubmit={handleSubmit} className="space-y-6" data-testid="contact-form">
                    <div className="grid md:grid-cols-2 gap-6">
                      <div className="space-y-2">
                        <Label htmlFor="name" className="text-[#1A1D20] font-medium">Name <span className="text-red-500">*</span></Label>
                        <Input id="name" name="name" value={formData.name} onChange={handleChange} placeholder="Your name"
                          className={`rounded-none border-[#E5E7EB] focus:border-[#0F2557] focus:ring-[#0F2557] h-12 transition-all duration-300 ${errors.name ? 'border-red-500' : ''}`}
                          data-testid="input-name" />
                        {errors.name && <p className="text-red-500 text-xs">{errors.name}</p>}
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="email" className="text-[#1A1D20] font-medium">Email <span className="text-red-500">*</span></Label>
                        <Input id="email" name="email" type="email" value={formData.email} onChange={handleChange} placeholder="your@email.com"
                          className={`rounded-none border-[#E5E7EB] focus:border-[#0F2557] focus:ring-[#0F2557] h-12 transition-all duration-300 ${errors.email ? 'border-red-500' : ''}`}
                          data-testid="input-email" />
                        {errors.email && <p className="text-red-500 text-xs">{errors.email}</p>}
                      </div>
                    </div>

                    <div className="grid md:grid-cols-2 gap-6">
                      <div className="space-y-2">
                        <Label htmlFor="phone" className="text-[#1A1D20] font-medium">Phone</Label>
                        <Input id="phone" name="phone" value={formData.phone} onChange={handleChange} placeholder="01234 567890"
                          className="rounded-none border-[#E5E7EB] focus:border-[#0F2557] focus:ring-[#0F2557] h-12 transition-all duration-300" data-testid="input-phone" />
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="company" className="text-[#1A1D20] font-medium">Company</Label>
                        <Input id="company" name="company" value={formData.company} onChange={handleChange} placeholder="Your company name"
                          className="rounded-none border-[#E5E7EB] focus:border-[#0F2557] focus:ring-[#0F2557] h-12 transition-all duration-300" data-testid="input-company" />
                      </div>
                    </div>

                    <div className="space-y-2">
                      <Label className="text-[#1A1D20] font-medium">Service Interest</Label>
                      <Select value={formData.service_interest} onValueChange={(value) => setFormData(prev => ({ ...prev, service_interest: value }))}>
                        <SelectTrigger className="rounded-none border-[#E5E7EB] focus:border-[#0F2557] focus:ring-[#0F2557] h-12" data-testid="select-service">
                          <SelectValue placeholder="Select a service" />
                        </SelectTrigger>
                        <SelectContent>
                          {serviceOptions.map((service) => (<SelectItem key={service} value={service}>{service}</SelectItem>))}
                        </SelectContent>
                      </Select>
                    </div>

                    <div className="space-y-2">
                      <Label htmlFor="message" className="text-[#1A1D20] font-medium">Message <span className="text-red-500">*</span></Label>
                      <Textarea id="message" name="message" value={formData.message} onChange={handleChange} placeholder="Tell us about your project requirements..." rows={5}
                        className={`rounded-none border-[#E5E7EB] focus:border-[#0F2557] focus:ring-[#0F2557] resize-none transition-all duration-300 ${errors.message ? 'border-red-500' : ''}`}
                        data-testid="input-message" />
                      {errors.message && <p className="text-red-500 text-xs">{errors.message}</p>}
                    </div>

                    <Button type="submit" disabled={isSubmitting} className="btn-premium w-full bg-[#0F2557] hover:bg-[#0F2557] text-white rounded-none py-7 text-lg font-bold" data-testid="submit-contact-form">
                      {isSubmitting ? (<><Loader2 className="mr-2 h-5 w-5 animate-spin" />Sending...</>) : (<><Send className="mr-2 h-5 w-5" />Send Enquiry</>)}
                    </Button>
                  </form>
                )}
              </div>
            </div>

            {/* Map & Social */}
            <div className="space-y-6 reveal-right">
              <div className="overflow-hidden border border-[#E5E7EB] shadow-xl" data-testid="map-container">
                <iframe src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d2356.5377823177!2d-0.33847492348856496!3d53.74839787259511!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x4878bec0c1e5b5c1%3A0x5c0c9b0e5c5c5c5c!2s82-84%20York%20St%2C%20Hull%20HU2%200QD%2C%20UK!5e0!3m2!1sen!2sus!4v1699999999999!5m2!1sen!2sus"
                  width="100%" height="300" style={{ border: 0 }} allowFullScreen="" loading="lazy" title="Fox Precision Engineering Location"></iframe>
              </div>

              <div className="p-8 bg-white border border-[#E5E7EB] shadow-xl transition-all duration-300 hover:shadow-2xl">
                <h3 className="text-xl font-bold text-[#1A1D20] mb-5">Connect With Us</h3>
                <a href="https://www.facebook.com/FoxPrecisionEngineeringLtd/" target="_blank" rel="noopener noreferrer"
                  className="inline-flex items-center gap-4 text-[#0F2557] hover:text-[#1a3a7a] transition-colors group" data-testid="facebook-page-link">
                  <div className="w-12 h-12 bg-[#0F2557] flex items-center justify-center transition-transform group-hover:scale-110">
                    <Facebook className="h-6 w-6 text-white" />
                  </div>
                  <span className="font-bold underline-animate">Follow us on Facebook</span>
                </a>
              </div>

              <div className="p-8 bg-[#0F2557] text-white shadow-xl pulse-glow">
                <h3 className="text-xl font-bold mb-4">Prefer to Talk?</h3>
                <p className="text-white/80 mb-6">Give us a call during business hours and we'll be happy to discuss your requirements.</p>
                <a href="tel:01482221098" className="btn-premium inline-flex items-center gap-3 px-8 py-4 bg-white text-[#0F2557] font-bold transition-all" data-testid="call-now-button">
                  <Phone className="h-5 w-5" />
                  01482 221098
                </a>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};
